import { MultiValue, SingleValue } from "react-select";
import { useMemo } from "react";
import { queryClient } from "@/ajax/queryClient";
import { StyledAsyncSelect } from "./StyledAsyncSelect";
import { trpc } from "@/trpc";

interface SpecialtiesSelectProps {
  value?: { name: string; id: string } | { name: string; id: string }[];
  onChange?: (
    value: { name: string; id: string } | { name: string; id: string }[] | null,
  ) => void;
  placeholder?: string;
  disabled?: boolean;
  useParent?: boolean;
  isMulti?: boolean;
  inputId?: string;
}

export const SpecialtiesSelectObject = ({
  value,
  onChange,
  isMulti = false,
  placeholder = "Select specialty",
  disabled = false,
  useParent = false,
  inputId,
}: SpecialtiesSelectProps) => {
  const specialtyOptionsResult = useMemo(() => {
    return queryClient.fetchQuery(
      trpc.healthcareProfessionalTypes.getMedicalSpecialties.queryOptions(
        undefined,
        {
          staleTime: 1000 * 60 * 5, // 5 minutes
          retry: 2,
        },
      ),
    );
  }, []);

  const loadSpecialties = async (inputValue: string) => {
    const specialtyOptions = await specialtyOptionsResult;
    const lowerInput = inputValue.toLowerCase();
    return specialtyOptions
      .filter((specialty) => (useParent ? specialty.isParent : true))
      .map((specialty) => ({
        label: specialty.name,
        value: specialty.id,
      }))
      .map((option) => ({
        ...option,
        score: option.label.toLowerCase().indexOf(lowerInput),
      }))
      .filter((option) => option.score >= 0)
      .sort((a, b) => {
        // Exact matches first
        if (a.score === 0 && b.score !== 0) {
          return -1;
        }
        if (b.score === 0 && a.score !== 0) {
          return 1;
        }
        // Then by position of match
        return a.score - b.score;
      })
      .map(({ label, value }) => ({ label, value }));
  };

  const handleChange = (
    option:
      | MultiValue<{ label: string; value: string }>
      | SingleValue<{ label: string; value: string }>
      | null,
  ) => {
    if (isMulti) {
      // For multi-select, option is an array of selected options
      const multiOption = option as MultiValue<{
        label: string;
        value: string;
      }>;
      onChange?.(
        multiOption?.map((o) => ({
          name: o.label,
          id: o.value,
        })) || [],
      );
    } else {
      // For single-select, option is a single object
      const singleOption = option as SingleValue<{
        label: string;
        value: string;
      }>;
      onChange?.(
        singleOption
          ? {
              name: singleOption.label,
              id: singleOption.value,
            }
          : null,
      );
    }
  };

  let inputValue = undefined;
  if (value) {
    if (Array.isArray(value)) {
      inputValue = value.map((item) => ({
        label: item.name,
        value: item.id,
      }));
    } else {
      inputValue = {
        label: value.name,
        value: value.id,
      };
    }
  }
  return (
    <StyledAsyncSelect
      inputId={inputId}
      value={inputValue}
      loadOptions={loadSpecialties}
      onChange={handleChange}
      defaultOptions={true}
      isDisabled={disabled}
      placeholder={placeholder}
      isMulti={isMulti}
      isClearable
    />
  );
};
